
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Download, RefreshCw, X, ArrowUp, ArrowDown } from "lucide-react";
import { exportToCSV } from "@/utils/exportUtils";
import { useMessageLogs, MessageLog } from "@/hooks/useMessageLogs";
import { DateRange } from "react-day-picker";
import { 
  Pagination, 
  PaginationContent, 
  PaginationEllipsis, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";

const MessageLogs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>("sent_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const { messageLogs, isLoading, refetch, clearLogs } = useMessageLogs({
    searchQuery,
    dateFrom: dateRange?.from,
    dateTo: dateRange?.to,
  });

  const filteredLogs = useMemo(() => {
    return messageLogs.filter(log => 
      log.phone_number.includes(searchQuery) || 
      log.quote_text.includes(searchQuery)
    );
  }, [messageLogs, searchQuery]);
  
  // Sort the logs
  const sortedLogs = useMemo(() => {
    return [...filteredLogs].sort((a, b) => {
      if (sortField === "sent_at") {
        return sortDirection === "asc" 
          ? new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
          : new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime();
      } else if (sortField === "phone_number") {
        return sortDirection === "asc"
          ? a.phone_number.localeCompare(b.phone_number)
          : b.phone_number.localeCompare(a.phone_number);
      } else if (sortField === "status") {
        return sortDirection === "asc"
          ? a.status.localeCompare(b.status)
          : b.status.localeCompare(a.status);
      }
      return 0;
    });
  }, [filteredLogs, sortField, sortDirection]);

  // Paginate the logs
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedLogs.slice(startIndex, startIndex + pageSize);
  }, [sortedLogs, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedLogs.length / pageSize);

  const handleExport = () => {
    exportToCSV(filteredLogs, 'message_logs');
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Message Logs</h1>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={refetch}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => {
              if (window.confirm("Are you sure you want to clear all logs?")) {
                clearLogs();
              }
            }}
          >
            <X className="mr-2 h-4 w-4" /> Clear Logs
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Input 
              placeholder="Search by phone or message..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="md:w-64"
            />
            <DatePicker 
              mode="range"
              selected={dateRange}
              onRangeSelect={handleDateRangeSelect}
              placeholder="Filter by date"
            />
            <div className="flex items-center space-x-2 ml-auto">
              <select 
                className="p-2 border rounded"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4">Loading logs...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-4">No message logs found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th 
                      className="text-left p-2 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort("phone_number")}
                    >
                      <div className="flex items-center">
                        Phone Number
                        {getSortIcon("phone_number")}
                      </div>
                    </th>
                    <th className="text-left p-2">Message</th>
                    <th className="text-left p-2">Category</th>
                    <th 
                      className="text-left p-2 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th 
                      className="text-left p-2 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort("sent_at")}
                    >
                      <div className="flex items-center">
                        Sent At
                        {getSortIcon("sent_at")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-100">
                      <td className="p-2">{log.phone_number}</td>
                      <td className="p-2">{log.quote_text}</td>
                      <td className="p-2">
                        {log.category_names && log.category_names.length > 0 
                          ? log.category_names.join(', ') 
                          : 'N/A'}
                      </td>
                      <td className="p-2">{log.status}</td>
                      <td className="p-2">{new Date(log.sent_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic to show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => setCurrentPage(totalPages)}>
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessageLogs;
